import React from 'react'

import { useDashboard } from '@/hooks'
import { celsiusToUser, tempUnitSuffix, userToCelsius } from '@/utils/conversions'

interface TemperatureInputProps {
  label: string
  value: string | number
  max?: number
  min?: number
  defaultValue?: number
  placeholder?: string
  onChange: (value?: string | number) => void
}

const toUserTemp = (units: 'Imperial' | 'Metric', temperature?: number) =>
  temperature !== undefined ? Number(celsiusToUser(temperature, 1, units)) : undefined

const TemperatureInput = ({
  label,
  value,
  placeholder = 'Enter a temperature value...',
  onChange,
  max,
  min,
  defaultValue,
}: TemperatureInputProps) => {
  const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
  const temperatureUnit = tempUnitSuffix(units)

  const userMaxValue = toUserTemp(units, max)
  const userMinValue = toUserTemp(units, min)
  const userDefaultValue = value
    ? Number(celsiusToUser(Number(value), 1, units))
    : Number(celsiusToUser(Number(defaultValue), 1, units))

  const [inputValue, setInputValue] = React.useState<number | undefined>(userDefaultValue ?? 0)

  const handleChange = (value?: string | number) => {
    const celsiusValue = userToCelsius(Number(value), 1, units)
    onChange(Math.round(celsiusValue))
  }

  return (
    <div className="mb-4">
      <label className="block text-base-content mb-2">
        {label} ({temperatureUnit})
      </label>
      <div className="flex flex-col sm:flex-row gap-4">
        <input
          type="number"
          max={userMaxValue}
          min={userMinValue}
          className="input input-bordered text-base border-base-300 w-[53%] focus:outline-none focus:border-primary/30"
          value={inputValue ? Math.round(inputValue) : ''}
          onChange={(e) => {
            // Only update the input state while typing
            const value = e.target.value ? parseInt(e.target.value, 10) : 0
            setInputValue(value)
          }}
          onBlur={() => {
            if (!inputValue) {
              handleChange(undefined)
              return
            }

            let newValue = inputValue

            if (Number.isNaN(newValue)) {
              handleChange(undefined)
              return
            }

            if (userMaxValue !== undefined && newValue > userMaxValue) {
              newValue = userMaxValue
              setInputValue(newValue)
            }

            if (userMinValue !== undefined && newValue < userMinValue) {
              newValue = userMinValue
              setInputValue(newValue)
            }

            handleChange(newValue)
          }}
          placeholder={placeholder}
          onFocus={(e) => e.target.select()}
          inputMode="numeric"
        />
      </div>
    </div>
  )
}

export default TemperatureInput
